import { useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import {
  Button,
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconVariants,
  Spinner,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Spacer } from "@nextui-org/react";
import { useState } from "react";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

import { getTextRatingLabels, RatingLabel } from "./constants";
import { ChatBubbleRatingProps, ChatRatings, MessageType } from "./types";
import { getLabelsBasedOnType, getRatingSentMessage } from "./utils";

const logger = appLogger.withTag("ChatBubbleRating");

function ChatBubbleRating({
  isSubmitting,
  setIsSubmitting,
  setLabelSentMessage,
  messageRating,
  setShowRatingBox,
  setIsRatingSent,
}: ChatBubbleRatingProps) {
  const { t } = useTranslation();
  const [ratingIndex, setRatingIndex] = useState(-1);
  const [ratingValue, setRatingValue] = useState<ChatRatings | "">("");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const { messageId, sendRating, messageType = MessageType.TEXT } = messageRating || {};
  const textRatingLabels = getTextRatingLabels(t);
  const advanceRatingLabels = getLabelsBasedOnType(messageType, t)?.[ratingValue];
  const ratingSentMessage = getRatingSentMessage(t, textRatingLabels[ratingValue as ChatRatings]) || "";

  const onStarClick = async ({
    ratingIndex,
    labelKey,
    ratingValue = "",
  }: {
    ratingIndex?: number;
    labelKey?: string;
    ratingValue?: ChatRatings | "";
  }) => {
    if (ratingIndex !== undefined && ratingIndex >= 0) {
      setRatingValue(ratingValue);
      setRatingIndex(ratingIndex);
    }

    if (labelKey) {
      setShowRatingBox(false);
    }
    setIsSubmitting(true);
    try {
      await sendRating?.(messageId || "", ratingValue || labelKey || "");
    } catch (error) {
      logger.error("Error in onStarClick", error);
    } finally {
      setIsSubmitting(false);
      if (labelKey) {
        setLabelSentMessage?.(ratingSentMessage);
      }
      setIsRatingSent(true);
    }
  };

  return (
    <Col className="p-[13px] rounded-[10px] border border-solid border-gray-6">
      <Row justify="between" align="center">
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
          <Trans i18nKey="chat:chatBubbleRating.title" />
        </Text>
        <div className="cursor-pointer" onClick={() => setShowRatingBox(false)}>
          <Icon icon={IconVariants.CLOSE} className="text-gray-11" />
        </div>
      </Row>
      <Row className="max-mob:flex-col mob:items-center">
        <Row className="-ml-md ">
          {Object.values(ChatRatings).map((value: ChatRatings, index: number) => (
            <div key={index} onMouseEnter={() => setHoverIndex(index)} onMouseLeave={() => setHoverIndex(null)}>
              <ButtonIcon
                icon={IconVariants.STAR}
                variant={ButtonVariant.TRANSPARENT}
                size={ButtonSize.MEDIUM}
                onClick={() => onStarClick({ ratingIndex: index, ratingValue: value })}
                className={`text-gray-12 ${index <= (hoverIndex ?? ratingIndex) ? "*:fill-gray-12" : ""}`}
                aria-label={`rate-${index}-star`}
              />
            </div>
          ))}
        </Row>
        {!isSubmitting && (
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-gray-12">
            {textRatingLabels[ratingValue as ChatRatings] || ""}
          </Text>
        )}
      </Row>
      <Spacer y={1} />
      {!isSubmitting && !!advanceRatingLabels?.length && (
        <Row className="gap-md flex-wrap">
          {advanceRatingLabels?.map((ratingLabel: RatingLabel, index: number) => (
            <Button
              key={index}
              variant={ButtonVariant.OUTLINED}
              size={ButtonSize.MEDIUM}
              className="!text-gray-11 dark:active:!bg-white dark:active:!text-black active:!bg-black active:!text-white "
              onClick={() => onStarClick({ labelKey: ratingLabel.key })}
              aria-label={`rate-${index}-star`}
            >
              {ratingLabel.label}
            </Button>
          ))}
        </Row>
      )}
      {isSubmitting ? <Spinner className="!size-5" /> : null}
    </Col>
  );
}

export default ChatBubbleRating;
